import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card } from '@dreamtree-org/twreact-ui';
import { CheckCircle2 } from 'lucide-react';
import { KeyValue, Money, QueryState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import { useStorefront } from '../StorefrontContext';
import CheckoutSteps from '../components/CheckoutSteps';
import { track } from '../lib/beacon';

/** WEB-05 order confirmation (step 4). */
export default function OrderConfirmed() {
  const { id } = useParams();
  const sf = useStorefront();
  const navigate = useNavigate();
  const q = useQuery('sfOrder', { id, seller: sf.seller.name }, { select: (r) => r.data });
  useEffect(() => track('purchase', { orderRef: id }), [id]);
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <CheckoutSteps current={4} />
      <QueryState query={q}>
        {(o) => (
          <Card className="space-y-5 p-5 sm:p-8">
            <div className="flex flex-col items-center gap-2 text-center">
              <CheckCircle2 className="h-14 w-14 text-success-600" aria-hidden="true" />
              <h1 className="text-2xl font-bold">Order placed</h1>
              <p className="opacity-80">Thank you. {o.seller.name} is packing your order. We'll SMS you at each step.</p>
            </div>
            <KeyValue
              items={[
                { label: 'Order number', value: o.id },
                { label: 'Placed on', value: formatDate(o.placed_at) },
                { label: 'Items', value: o.items.map((i) => { const t = i.variant_label ? `${i.title} (${i.variant_label})` : i.title; return i.qty > 1 ? `${t} × ${i.qty}` : t; }).join(', ') },
                { label: 'Deliver to', value: o.address },
                { label: 'Expected by', value: o.shipment && o.shipment.eta ? formatDate(o.shipment.eta) : 'We will confirm shortly' },
                { label: 'Paid', value: <><Money paise={o.totals.total_paise} /> by {o.payment.method}</> },
              ]}
            />
            <div className="grid gap-2 sm:grid-cols-2">
              <Button fullWidth onClick={() => navigate(`/account/orders/${o.id}`)}>Track this order</Button>
              <Button fullWidth variant="outline" onClick={() => navigate('/shop')}>Continue shopping</Button>
            </div>
          </Card>
        )}
      </QueryState>
    </div>
  );
}
